import {useMediaQuery} from '@mui/material'
import {FC, MouseEvent, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {
  clearChatRequests,
  clearNewChatRequest,
  GetChatRequests,
  useChatRequestsCursor,
  useChatRequestsList,
  useChatRequestsNew
} from 'entities/activity/model/chatRequests'
import {ActivityUser, ChatRequestType} from 'entities/activity/types'
import {useChatRequestsHide, useConnectSocket} from 'entities/app/model'
import {useDebounce, useVirtualScroll} from 'shared/hooks'
import {mediaVariants} from 'shared/lib'
import {Avatar} from 'shared/ui/avatar'
import {Button} from 'shared/ui/buttons'
import {BullseyeArrow, Message} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import styles from './styles.module.scss'

const avatarHoverTopInitPx = -48
const heightImagePx = 153
const heightItemPx = 60

type ChatRequestsListProps = {
  onClose?: () => void
}

export const ChatRequestsList: FC<ChatRequestsListProps>  = ({onClose}) => {
  const dispatch = useDispatch()
  const list = useChatRequestsList()
  const cursor = useChatRequestsCursor()
  const [ref, setRef] = useState<HTMLDivElement | null>(null)
  const {refetch: load, isFetching, isError} = GetChatRequests(dispatch)
  const newChatRequests = useChatRequestsNew()
  const connectSocket = useConnectSocket()

  useVirtualScroll({ref, cursor, load, isFetching, isError})

  useEffect(() => {
    dispatch(clearNewChatRequest())
  }, [])

  useEffect(() => {
    dispatch(clearChatRequests())
  }, [connectSocket])

  return (
    <div className={`${styles.chatRequestsList} ${styles.customScroll}`} ref={setRef}>
      {list.map((chatRequest, index) => (
        <ChatRequestItem
          key={chatRequest.userFrom.id}
          reply='filled'
          messageText={chatRequest.content.message}
          userFrom={chatRequest.userFrom}
          type={chatRequest.type}
          chatRequestsListRef={ref}
          index={index}
          onClose={onClose}
          isNew={newChatRequests.includes(chatRequest.userFrom.id)}
        />
      ))}
      {!list.length && !isFetching
        ? <div className={styles.notCharRequests}>You don't have any chat requests yet</div>
        : null}
      {isFetching ? <div className={styles.loader}><Loader /></div> : null}
    </div>
  )
}

type ChatRequestItemProps = {
  type?: ChatRequestType
  messageText?: string
  reply?: 'filled' | 'outline'
  userFrom: ActivityUser
  chatRequestsListRef: HTMLDivElement | null
  index: number
  onClose?: () => void
  isNew: boolean
}

const ChatRequestItem: FC<ChatRequestItemProps> = ({
  type,
  reply,
  messageText,
  userFrom,
  chatRequestsListRef,
  index,
  onClose,
  isNew
}) => {
  const navigate = useNavigate()
  const is1280p = useMediaQuery(`(max-width: ${styles.is1280p})`)
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const isHide = useChatRequestsHide()
  const [isErrorImg, setIsErrorImg] = useState(false)

  const topHover = useMemo(() => {
    if (!chatRequestsListRef) return

    const scrollTop = chatRequestsListRef.scrollTop
    const clientHeight = chatRequestsListRef.clientHeight

    let top = avatarHoverTopInitPx - scrollTop
    const outline = clientHeight - index * heightItemPx - heightImagePx + scrollTop - avatarHoverTopInitPx

    if (outline + heightImagePx > clientHeight) {
      top += heightItemPx
    }

    if (outline < 0) {
      top += outline
    }

    return top
  }, [chatRequestsListRef?.scrollTop, useDebounce(isHide, 300)])

  const handleReply = () => {
    navigate(`/messages/${userFrom.id}`)
    onClose && onClose()
  }

  const onOpenProfile = (event: MouseEvent<HTMLSpanElement>) => {
    if (is768p) return

    event.stopPropagation()

    navigate(`/profile/${userFrom.id}`)
  }

  return (
    <div className={`${styles.chatRequestItem} ${isNew ? styles.new : ''}`} onClick={handleReply}>
      <div className={styles.avatar}>
        <Avatar sx={is1280p ? {width: 52, height: 52} : {}} src={userFrom.avatarUrl + mediaVariants.square64} alt={userFrom.name} />
        <div className={styles.badge}>
          {type === ChatRequestType.MESSAGE ? <div className={styles.message}>
            <Message />
          </div> : null}
          {type === ChatRequestType.SUPER_MATCH ? <div className={styles.match}>
            <BullseyeArrow />
          </div> : null}
        </div>
      </div>

      {!isErrorImg && userFrom.avatarUrl && !is1280p ? <div className={styles.avatarHoverWrapper} style={{top: topHover}}>
        <div className={styles.avatarHover}>
          <img src={userFrom.avatarUrl + mediaVariants.square256} alt={userFrom.name} onError={() => setIsErrorImg(true)} />
        </div>
      </div> : null}

      <div className={styles.chatRequestInfo}>
        <div className={styles.info}>
          <div className={styles.name}>
            <span onClick={onOpenProfile}>{userFrom.name}</span>
          </div>
          <div className={styles.message}>
            <span className={styles.text}>{messageText}</span>
          </div>
        </div>

        {type === ChatRequestType.MESSAGE ? <Button
          styleType={`${reply === 'filled' ? 'primary' : 'secondary'}`}
          className={styles.replyButton}
        >
          <div className={`${styles.back} ${isNew ? styles.animate : ''}`} />
          <span>Reply</span>
        </Button> : null}
      </div>
    </div>
  )
}